<template>
  <v-card>
    <v-container class="py-0" fluid>
      <v-row>
        <v-col cols="12">
          <v-card-title class="pa-0">
            <div class="headline">Pipeline Health</div>
          </v-card-title>
        </v-col>
      </v-row>

      <!-- Section 1: KPI Summary Cards -->
      <v-row v-if="organization">
        <v-col
          v-for="stage in pipelineStages"
          :key="stage.name"
          cols="6"
          sm="4"
          md="2"
        >
          <v-card :color="stage.color" dark class="text-center pa-3">
            <div class="text-h5 font-weight-bold">{{ stage.total }}</div>
            <div class="caption">{{ stage.name }}</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Section 2: Pipeline Trend (stacked area) -->
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card>
            <v-toolbar flat dense>
              <span class="text-subtitle-1"
                >Pipeline Trend (Cases by Stage)</span
              >
              <v-spacer></v-spacer>
              <v-btn-toggle
                v-model="trendViewMode"
                mandatory
                dense
                class="mt-0 pt-0"
              >
                <v-btn small value="chart" title="Chart View">
                  <v-icon small>mdi-chart-areaspline</v-icon>
                </v-btn>
                <v-btn small value="table" title="Table View">
                  <v-icon small>mdi-table</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-toolbar>
            <v-progress-linear
              v-if="$apollo.queries.pipelineByStageByMonth.loading"
              indeterminate
            ></v-progress-linear>
            <div v-if="trendViewMode === 'chart'" class="chart-vis">
              <pipeline-trend-chart :options="pipelineTrendOptions" />
            </div>
            <div v-else>
              <v-data-table
                :headers="trendTableHeaders"
                :items="trendTableItems"
                :sort-by="['month']"
                dense
                :footer-props="{
                  'items-per-page-options': [12, 24, -1],
                }"
              ></v-data-table>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Section 3: Current Inventory by Attorney -->
      <v-row>
        <v-col sm="12" md="8">
          <v-card>
            <v-toolbar flat dense>
              <span class="text-subtitle-1">Pre-lit Inventory</span>
              <v-spacer></v-spacer>
              <v-switch
                v-model="showActiveOnly"
                label="Active Only"
                dense
                hide-details
                class="mt-0 pt-0"
              ></v-switch>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-btn-toggle
                v-model="inventoryViewMode"
                mandatory
                dense
                class="mt-0 pt-0"
              >
                <v-btn small value="chart" title="Chart View">
                  <v-icon small>mdi-chart-bar</v-icon>
                </v-btn>
                <v-btn small value="table" title="Table View">
                  <v-icon small>mdi-table</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-toolbar>
            <div class="chart-vis" v-if="inventoryViewMode === 'chart'">
              <inventory-by-user :options="inventoryByPreLitAttorney" />
            </div>
            <div v-else>
              <v-data-table
                :headers="getTableHeaders(inventoryByPreLitAttorney)"
                :items="getTableItems(inventoryByPreLitAttorney)"
                :sort-by="['total']"
                :sort-desc="[true]"
                dense
                :footer-props="{
                  'items-per-page-options': [10, 25, -1],
                }"
              ></v-data-table>
            </div>
          </v-card>
        </v-col>
        <v-col sm="12" md="4">
          <v-card>
            <v-toolbar flat dense>
              <span class="text-subtitle-1">Lit Inventory</span>
              <v-spacer></v-spacer>
              <v-switch
                v-model="showActiveOnly"
                label="Active Only"
                dense
                hide-details
                class="mt-0 pt-0"
              ></v-switch>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-btn-toggle
                v-model="inventoryViewMode"
                mandatory
                dense
                class="mt-0 pt-0"
              >
                <v-btn small value="chart" title="Chart View">
                  <v-icon small>mdi-chart-bar</v-icon>
                </v-btn>
                <v-btn small value="table" title="Table View">
                  <v-icon small>mdi-table</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-toolbar>
            <div class="chart-vis" v-if="inventoryViewMode === 'chart'">
              <inventory-by-user :options="inventoryByLitAttorney" />
            </div>
            <div v-else>
              <v-data-table
                :headers="getTableHeaders(inventoryByLitAttorney)"
                :items="getTableItems(inventoryByLitAttorney)"
                :sort-by="['total']"
                :sort-desc="[true]"
                dense
                :footer-props="{
                  'items-per-page-options': [10, 25, -1],
                }"
              ></v-data-table>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Section 4: Staff Pipeline Trend -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-toolbar flat dense>
              <span class="text-subtitle-1">Staff Pipeline Trend</span>
              <v-spacer></v-spacer>
              <v-autocomplete
                v-model="selectedStaff"
                :items="staffList"
                label="Select Attorney"
                dense
                hide-details
                clearable
                style="max-width: 200px"
                class="mt-0 pt-0"
              ></v-autocomplete>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-btn-toggle
                v-model="staffTrendViewMode"
                mandatory
                dense
                class="mt-0 pt-0"
              >
                <v-btn small value="chart" title="Chart View">
                  <v-icon small>mdi-chart-areaspline</v-icon>
                </v-btn>
                <v-btn small value="table" title="Table View">
                  <v-icon small>mdi-table</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-toolbar>
            <v-progress-linear
              v-if="$apollo.queries.pipelineByStageByMonthByStaff.loading"
              indeterminate
            ></v-progress-linear>
            <div
              v-if="!selectedStaff"
              class="text-center pa-6 text--secondary"
            >
              Select an attorney to view their pipeline trend
            </div>
            <div
              v-else-if="staffTrendViewMode === 'chart'"
              class="chart-vis"
            >
              <pipeline-trend-chart :options="staffPipelineTrendOptions" />
            </div>
            <div v-else>
              <v-data-table
                :headers="trendTableHeaders"
                :items="staffTrendTableItems"
                :sort-by="['month']"
                dense
                :footer-props="{
                  'items-per-page-options': [12, 24, -1],
                }"
              ></v-data-table>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import { mapGetters } from "vuex";
import PipelineTrendChart from "../charts/PipelineTrendChart";
import InventoryByUser from "../charts/InventoryByUser";
import { getPreLitAttorneysDb, getLitAttorneysDb } from "../util";

const STAGE_CONFIG = [
  { id: 3, name: "Intake", color: "#2196F3", field: "totalIntakeCases" },
  { id: 10, name: "Treating", color: "#4CAF50", field: "totalTreatingCases" },
  {
    id: 11,
    name: "Demand Prep",
    color: "#FFC107",
    field: "totalDemandPrepCases",
  },
  {
    id: 1,
    name: "Negotiation",
    color: "#FF9800",
    field: "totalNegotiationCases",
  },
  {
    id: 2,
    name: "Litigation",
    color: "#F44336",
    field: "totalLitigationCases",
  },
  { id: 4, name: "Settled", color: "#009688", field: "totalSettledCases" },
];

export default {
  components: {
    PipelineTrendChart,
    InventoryByUser,
  },
  data() {
    return {
      trendViewMode: "chart",
      inventoryViewMode: "chart",
      staffTrendViewMode: "chart",
      showActiveOnly: false,
      selectedStaff: null,
    };
  },
  apollo: {
    organization: {
      query: gql`
        query OrgInventoryDashboard($id: ID!) {
          organization(id: $id) {
            id
            name
            departments {
              id
              name
              users {
                staffCode
              }
            }
            inventoryByAttorney {
              staff
              totalIntakeCases
              totalTreatingCases
              totalDemandPrepCases
              totalNegotiationCases
              totalSettledCases
              totalUimCases
              totalReferredOutCases
              totalLitigationCases
              totalClosedCases
              totalNoStatusCases
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id,
        };
      },
    },
    pipelineByStageByMonth: {
      query: gql`
        query PipelineByStage($startDate: String!, $endDate: String!) {
          pipelineByStageByMonth(startDate: $startDate, endDate: $endDate) {
            year
            month
            yearMonth
            stageId
            count
          }
        }
      `,
      variables() {
        return {
          startDate: this.startDate,
          endDate: this.endDate,
        };
      },
      skip() {
        return this.startDate === null || this.endDate === null;
      },
    },
    pipelineByStageByMonthByStaff: {
      query: gql`
        query PipelineByStageByStaff(
          $startDate: String!
          $endDate: String!
        ) {
          pipelineByStageByMonthByStaff(
            startDate: $startDate
            endDate: $endDate
          ) {
            year
            month
            yearMonth
            stageId
            attorney
            count
          }
        }
      `,
      variables() {
        return {
          startDate: this.startDate,
          endDate: this.endDate,
        };
      },
      skip() {
        return this.startDate === null || this.endDate === null;
      },
    },
  },
  computed: {
    ...mapGetters(["startDate", "endDate"]),

    // Section 1: KPI cards
    pipelineStages() {
      if (!this.organization) return [];
      let iba = this.organization.inventoryByAttorney;
      return STAGE_CONFIG.map((s) => ({
        name: s.name,
        color: s.color,
        total: iba.reduce((sum, row) => sum + (row[s.field] || 0), 0),
      }));
    },

    // Section 2: Pipeline trend chart options
    pipelineTrendOptions() {
      return this.buildTrendOptions(this.pipelineByStageByMonth);
    },

    // Section 2: Table headers
    trendTableHeaders() {
      return [
        { text: "Month", value: "month", sortable: true },
        ...STAGE_CONFIG.map((s) => ({
          text: s.name,
          value: s.name.toLowerCase().replace(/\s+/g, "_"),
          sortable: true,
        })),
        { text: "Total", value: "total", sortable: true },
      ];
    },

    // Section 2: Table items
    trendTableItems() {
      return this.buildTrendTableItems(this.pipelineByStageByMonth);
    },

    // Section 3: Pre-lit inventory by attorney
    inventoryByPreLitAttorney() {
      if (!this.organization) return {};
      let iba = this.organization.inventoryByAttorney;
      let attorneys = getPreLitAttorneysDb(this.organization);
      let series = this.buildFilteredInventorySeries(iba, attorneys);
      return {
        xAxis: {
          categories: attorneys,
        },
        series,
        plotOptions: {
          column: {
            stacking: "normal",
            reversedStacks: false,
          },
        },
      };
    },

    // Section 3: Lit inventory by attorney
    inventoryByLitAttorney() {
      if (!this.organization) return {};
      let iba = this.organization.inventoryByAttorney;
      let attorneys = getLitAttorneysDb(this.organization);
      let series = this.buildFilteredInventorySeries(iba, attorneys);
      return {
        xAxis: {
          categories: attorneys,
        },
        series,
        plotOptions: {
          column: {
            stacking: "normal",
            reversedStacks: false,
          },
        },
      };
    },

    // Section 4: Staff list for dropdown
    staffList() {
      if (!this.pipelineByStageByMonthByStaff) return [];
      return sortBy([
        ...new Set(
          this.pipelineByStageByMonthByStaff
            .map((d) => d.attorney)
            .filter(Boolean)
        ),
      ]);
    },

    // Section 4: Selected staff trend chart options
    staffPipelineTrendOptions() {
      if (!this.pipelineByStageByMonthByStaff || !this.selectedStaff)
        return {};
      let filtered = this.pipelineByStageByMonthByStaff.filter(
        (d) => d.attorney === this.selectedStaff
      );
      return this.buildTrendOptions(filtered);
    },

    // Section 4: Staff table items
    staffTrendTableItems() {
      if (!this.pipelineByStageByMonthByStaff || !this.selectedStaff)
        return [];
      let filtered = this.pipelineByStageByMonthByStaff.filter(
        (d) => d.attorney === this.selectedStaff
      );
      return this.buildTrendTableItems(filtered);
    },
  },
  methods: {
    buildTrendOptions(data) {
      if (!data || data.length === 0) return {};

      // Get sorted unique months
      let monthKeys = [
        ...new Set(data.map((d) => `${d.year}-${String(d.month).padStart(2, "0")}`)),
      ].sort();

      let categories = monthKeys.map((m) => {
        let [year, month] = m.split("-").map(Number);
        let date = new Date(year, month - 1, 1);
        return date.toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
      });

      // Group by stage
      let byStage = groupBy(data, (d) => d.stageId);

      let series = STAGE_CONFIG.map((stage) => {
        let stageData = byStage[stage.id] || [];
        let byMonth = {};
        stageData.forEach((d) => {
          let key = `${d.year}-${String(d.month).padStart(2, "0")}`;
          byMonth[key] = (byMonth[key] || 0) + d.count;
        });
        return {
          name: stage.name,
          color: stage.color,
          data: monthKeys.map((m) => byMonth[m] || 0),
        };
      });

      return {
        xAxis: {
          categories,
        },
        series,
      };
    },

    buildTrendTableItems(data) {
      if (!data || data.length === 0) return [];

      let monthKeys = [
        ...new Set(data.map((d) => `${d.year}-${String(d.month).padStart(2, "0")}`)),
      ].sort();

      let byStage = groupBy(data, (d) => d.stageId);

      return monthKeys.map((m) => {
        let [year, month] = m.split("-").map(Number);
        let label = new Date(year, month - 1, 1).toLocaleString("default", {
          month: "short",
          year: "numeric",
        });
        let item = { month: label, total: 0 };
        STAGE_CONFIG.forEach((stage) => {
          let stageData = byStage[stage.id] || [];
          let key = stage.name.toLowerCase().replace(/\s+/g, "_");
          let entry = stageData.find(
            (d) =>
              d.year === year && d.month === month
          );
          let count = entry ? entry.count : 0;
          item[key] = count;
          item.total += count;
        });
        return item;
      });
    },

    buildFilteredInventorySeries(data, categories) {
      let groupedByAttorney = groupBy(data, (d) => d.staff);

      let seriesDefinitions = [
        {
          name: "No Status",
          value: "totalNoStatusCases",
          color: "#9E9E9E",
        },
        { name: "Intake", value: "totalIntakeCases", color: "#2196F3" },
        {
          name: "Treating",
          value: "totalTreatingCases",
          color: "#4CAF50",
        },
        {
          name: "Demand Prep",
          value: "totalDemandPrepCases",
          color: "#FFC107",
        },
        {
          name: "Negotiation",
          value: "totalNegotiationCases",
          color: "#FF9800",
        },
        {
          name: "Litigation",
          value: "totalLitigationCases",
          color: "#F44336",
        },
        { name: "UIM", value: "totalUimCases", color: "#9C27B0" },
      ];

      if (!this.showActiveOnly) {
        seriesDefinitions = seriesDefinitions.concat([
          {
            name: "Referred Out",
            value: "totalReferredOutCases",
            color: "#795548",
          },
          {
            name: "Closed",
            value: "totalClosedCases",
            color: "#607D8B",
          },
          {
            name: "Settled",
            value: "totalSettledCases",
            color: "#009688",
          },
        ]);
      }

      let series = seriesDefinitions.map((s) => {
        return {
          name: s.name,
          color: s.color,
          data: categories.reduce((prev, cur) => {
            if (!groupedByAttorney[cur]) {
              return prev.concat(0);
            } else {
              return prev.concat(groupedByAttorney[cur][0][s.value]);
            }
          }, []),
        };
      });

      return series.reverse();
    },

    getTableHeaders(chartOptions) {
      if (!chartOptions.xAxis || !chartOptions.series) {
        return [];
      }
      let headers = [
        { text: "Attorney", value: "attorney", sortable: true },
      ];
      chartOptions.series.forEach((series) => {
        headers.push({
          text: series.name,
          value: series.name.toLowerCase().replace(/\s+/g, "_"),
          sortable: true,
        });
      });
      headers.push({ text: "Total", value: "total", sortable: true });
      return headers;
    },

    getTableItems(chartOptions) {
      if (!chartOptions.xAxis || !chartOptions.series) {
        return [];
      }
      const categories = chartOptions.xAxis.categories;
      const series = chartOptions.series;
      return categories.map((attorney, index) => {
        let item = { attorney, total: 0 };
        series.forEach((s) => {
          const key = s.name.toLowerCase().replace(/\s+/g, "_");
          const value = s.data[index] || 0;
          item[key] = value;
          item.total += value;
        });
        return item;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.chart-vis {
  min-height: 250px;
}
</style>
